import { ButtonGroup, ContentWrapper, spacing, Text } from '@rulebooks/evidend-titan';
import { useFormLeaveConfirmRoute } from 'components/LeavePrompt';
import { PageLoader } from 'components/PageLoader/PageLoader';
import { throttle } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import { useEffectOnce, useEvent } from 'react-use';
import styled from 'styled-components';

interface IWizardFooter {
  isLoading?: boolean;
  /** Usually plain instruction text, but the summary step passes an emphasised fee sentence. */
  label: React.ReactNode;
  /** Right by default; the summary step's longer fee sentence reads better left-aligned. */
  labelAlign?: 'left' | 'right';
  children: React.ReactNode;
}

const StyledInstructions = styled.div<{ $labelAlign: 'left' | 'right' }>`
  flex: 1 1 auto;
  text-align: ${({ $labelAlign }) => $labelAlign};
`;

const StyledText = styled(Text)`
  margin-left: auto;
  max-width: 480px;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  text-align: right;
  align-items: center;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  flex: 0 1 auto;
  margin-left: ${spacing(6)};
`;

/**
 * FullscreenLayoutFooter in Titan uses react-unstuck, which only supports stick to top.
 * Titan works around this by calculating the window height and subtracting the footer height,
 * but this apparently doesn't work on iPads which report an incorrect window height.
 *
 * Instead, we use a custom implementation that uses CSS to stick the footer to the bottom.
 */
const FullscreenLayoutFooter = styled(ContentWrapper)`
  background: ${({ theme }) => theme.components.fullscreenLayout.backgroundColor};
  border-top: 1px solid ${({ theme }) => theme.components.fullscreenLayout.footerBorderColor};
  padding: ${({ theme }) => theme.components.fullscreenLayout.footerPaddingVertical} 0;
  margin: 0 auto;
  position: fixed;
  bottom: 0;
  width: 100%;
`;

/**
 * We need a placeholder for the footer so the actual footer doesn't slide over the content
 * above it.
 */
interface IPlaceholder {
  height: number;
}
const Placeholder = styled.div<IPlaceholder>`
  height: ${(props) => props.height}px;
  /* Make sure the div is always rendered. */
  content: '200b';
`;

export const WizardFooter = ({
  isLoading,
  label,
  labelAlign = 'right',
  children,
}: IWizardFooter) => {
  // We want to enable this globally for all wizards. Since the footer is
  // always within the form context, and the leave prompt functionality only
  // makes sense if there *is* a footer with a save button, this is a
  // logical place to add this.
  useFormLeaveConfirmRoute();
  const placeholderRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLElement>(null);

  const [width, setWidth] = useState(600);
  const [height, setHeight] = useState(0);

  const onResize = useCallback(() => {
    if (placeholderRef.current) {
      setWidth(placeholderRef.current.offsetWidth);
    }
    if (footerRef.current) {
      setHeight(footerRef.current.offsetHeight);
    }
  }, []);

  useEffectOnce(onResize);

  const throttledResize = throttle(onResize, 250);
  useEvent('resize', throttledResize);

  return (
    <>
      <Placeholder ref={placeholderRef as any} height={height} />

      {isLoading && <PageLoader hasBackground isAnimated />}

      <FullscreenLayoutFooter ref={footerRef} maxWidth={width}>
        <StyledFooter>
          <StyledInstructions $labelAlign={labelAlign}>
            <StyledText color="grey70" type="labelSmall">
              {label}
            </StyledText>
          </StyledInstructions>
          <StyledButtonGroup spaceBetween={2}>{children}</StyledButtonGroup>
        </StyledFooter>
      </FullscreenLayoutFooter>
    </>
  );
};
